import { clsx } from 'clsx'
import { useEffect, useRef, useState } from 'react'

type Props = {
  done: number
  total: number
  label?: string
  className?: string
  size?: 'lg' | 'sm'
  /** False while the numbers are still loading: the block keeps its space but stays hidden, so nothing counts up from 0. */
  ready?: boolean
}

const DURATION = 500
// Approximates --ease-out-soft (cubic-bezier(0.22, 1, 0.36, 1)) so the numeral tracks the bar.
const easeOutSoft = (t: number) => 1 - Math.pow(1 - t, 5)

/**
 * Counts from the last shown value to `target` over the bar's transition. The first real value snaps
 * into place: data arrives after the first paint, and counting up from 0 on every page visit reads as
 * the number changing when nothing did. Only later changes animate.
 */
function useCountTo(target: number, hasData: boolean) {
  const [shown, setShown] = useState(target)
  const from = useRef(target)
  const settled = useRef(hasData)
  useEffect(() => {
    const start = from.current
    const snap = !settled.current || start === target || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (hasData) settled.current = true
    if (snap) {
      from.current = target
      setShown(target)
      return
    }
    const t0 = performance.now()
    let raf = requestAnimationFrame(function tick(now) {
      const t = Math.min(1, (now - t0) / DURATION)
      const v = Math.round(start + (target - start) * easeOutSoft(t))
      from.current = v
      setShown(v)
      if (t < 1) raf = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(raf)
  }, [target])
  return shown
}

/** Big serif numeral with a hairline bar. Announced as a progressbar. */
export function ProgressNumber({ done, total, label = 'Progress', className, size = 'lg', ready = true }: Props) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  const shown = useCountTo(pct, ready && total > 0)
  return (
    <div className={clsx('grid gap-2', !ready && 'invisible', className)} aria-busy={!ready || undefined}>
      <div className="flex items-baseline gap-2">
        <span className={clsx('font-display text-ink tabular-nums', size === 'lg' ? 'text-[2.75rem] lg:text-numeral' : 'text-h3')}>{shown}</span>
        <span className="font-display text-dim text-h5">%</span>
        <span className="ml-auto text-small text-dim tabular-nums">
          {done} of {total}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={`${done} of ${total} complete`}
        className="h-px w-full bg-gray-300"
      >
        {/* Width follows the counted value, so the bar and numeral move together and snap together. */}
        <div className="h-px bg-ink" style={{ width: `${shown}%` }} />
      </div>
    </div>
  )
}

/** A secondary count under a ProgressNumber: eyebrow label and "n of m" on one line, hairline bar beneath. */
export function ProgressRow({ done, total, label, className, ready = true }: Omit<Props, 'size' | 'label'> & { label: string }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  return (
    <div className={clsx('grid gap-2', !ready && 'invisible', className)} aria-busy={!ready || undefined}>
      <div className="flex items-baseline gap-3">
        <span className="text-eyebrow uppercase tracking-eyebrow text-ink">{label}</span>
        <span className="ml-auto text-small text-dim tabular-nums">
          {done} of {total}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={`${done} of ${total}`}
        className="h-px w-full bg-gray-300"
      >
        <div className="h-px bg-ink transition-[width] duration-500 ease-out-soft" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * Progress for a pinned bar (the steps' bottom bar below lg, where the rail's numeral has scrolled away): the
 * percentage, its label and "n of m" on one line, with the bar itself laid over the pinned bar's top edge.
 * Hidden from screen readers: it repeats the rail's progressbar, which stays on the page.
 */
export function ProgressStrip({ done, total, label, className, ready = true }: Omit<Props, 'size' | 'label'> & { label: string }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  return (
    <div aria-hidden="true" className={clsx('flex items-baseline gap-2 text-small', (!ready || total === 0) && 'invisible', className)}>
      <span className="absolute inset-x-0 top-0 h-0.5 -translate-y-px bg-gray-300">
        <span className="block h-full bg-ink transition-[width] duration-500 ease-out-soft" style={{ width: `${pct}%` }} />
      </span>
      <span className="font-medium text-ink tabular-nums">{pct}%</span>
      <span className="text-[0.6875rem] uppercase tracking-eyebrow text-dim">{label}</span>
      <span className="ml-auto text-dim tabular-nums">
        {done} of {total}
      </span>
    </div>
  )
}
